import {
  Action,
  config,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import {
  BrowseModeSnapshotData,
  BuilderSnapshotData,
  SnapshotData,
  SnapshotInfo,
  SnapshotOptionalInfo,
  TakeBrowseModeSnapshotArgs,
  TakeBuilderSnapshotArgs
} from "@/types/snapshot";
import { Operation, applyPatch } from "fast-json-patch";
import defensiveCopy from "@/util/defensive-copy";
import { compare } from "rfc6902-json-diff";

const snapshotDataDiffs = new Map<number, Operation[]>();

let snapshotIdCounter = 0;

function _snapshotInfoFromSnapshotData(
  snapshot: SnapshotOptionalInfo
): SnapshotInfo {
  return {
    id: ++snapshotIdCounter,
    key: snapshot.key,
    title: snapshot.title
  };
}

function generateDiffBetweenTwoSnapshotDatas(
  maybeOldSnapshotData: SnapshotData | undefined,
  newSnapshotData: SnapshotData
): Operation[] {
  return compare(maybeOldSnapshotData || {}, newSnapshotData);
}

function regenerateSnapshotDataForIndex<T extends SnapshotData>(
  idx: number,
  snapshotInfos: SnapshotInfo[]
): T | undefined {
  const currentSnapshotDataId = snapshotInfos[idx]?.id;

  if (!currentSnapshotDataId) {
    return undefined;
  }

  const snapshot = {};

  snapshotInfos.forEach((value, key) => {
    if (key <= idx) {
      const snapshotDiff = defensiveCopy(snapshotDataDiffs.get(value.id));
      if (snapshotDiff) {
        applyPatch(snapshot, snapshotDiff);
      }
    }
  });

  // IO-TS decoding is required here for type-safety, but this function should work as fast as possible.
  // Heavy decoding will slow down undo/redo significantly
  return snapshot as T;
}

config.rawError = true;
@Module({ name: "snapshot", namespaced: true })
export default class SnapshotModule extends VuexModule {
  index = -1;
  snapshots: SnapshotInfo[] = [];

  get canUndo() {
    return this.index > 0;
  }

  get canRedo() {
    return this.index !== -1 && this.index + 1 < this.snapshots.length;
  }

  @Mutation
  clear() {
    this.index = -1;
    this.snapshots = [];
  }

  @Action
  undoIndex() {
    this.setCurrentSnapshotIndex(this.index - 1);
  }

  @Action
  redoIndex() {
    this.setCurrentSnapshotIndex(this.index + 1);
  }

  @Action
  takeBuilderSnapshot({
    document,
    globalSettings,
    cssSelectors,
    presets,
    key,
    activeElementId,
    activeSelectorName,
    panelState,
    title
  }: TakeBuilderSnapshotArgs) {
    if (this.canRedo) {
      this.setCurrentSnapshotIndex(this.index);
      this.rewriteHistoryFromIndex(this.index + 1);
    }

    const newSnapshot: BuilderSnapshotData = {
      document,
      globalSettings,
      cssSelectors,
      activeElementId,
      activeSelectorName,
      panelState,
      presets,
      key
    };

    // When changing the same property for the same element we take a snapshot
    // only for the last change
    if (
      this.currentSnapshotInfo &&
      key &&
      key === this.currentSnapshotInfo.key
    ) {
      this.overrideLastSnapshotMutation({ ...newSnapshot, title });
    } else {
      this.setCurrentSnapshotIndex(this.snapshots.length);
      this.snapshotMutation({ ...newSnapshot, title });
    }
  }

  @Action
  takeBrowseModeSnapshot({
    globalSettings,
    cssSelectors,
    presets,
    key,
    activeSelectorName,
    panelState,
    title
  }: TakeBrowseModeSnapshotArgs) {
    if (this.canRedo) {
      this.setCurrentSnapshotIndex(this.index);
      this.rewriteHistoryFromIndex(this.index + 1);
    }

    const newSnapshot: BrowseModeSnapshotData = {
      globalSettings,
      cssSelectors,
      presets,
      activeSelectorName,
      panelState,
      key
    };

    // When changing the same property for the same element we take a snapshot
    // only for the last change
    if (
      this.currentSnapshotInfo &&
      key &&
      key === this.currentSnapshotInfo.key
    ) {
      this.overrideLastSnapshotMutation({ ...newSnapshot, title });
    } else {
      this.setCurrentSnapshotIndex(this.snapshots.length);
      this.snapshotMutation({ ...newSnapshot, title });
    }
  }

  @Mutation
  setCurrentSnapshotIndex(index: number) {
    this.index = index;
  }

  @Mutation
  snapshotMutation(newSnapshotData: SnapshotData) {
    const snapshotInfo = _snapshotInfoFromSnapshotData(newSnapshotData);
    this.snapshots.push(snapshotInfo);

    const maybeCurrentSnapshotData = regenerateSnapshotDataForIndex(
      this.index,
      this.snapshots
    );

    const diffBetweenCurrentAndNewSnapshots =
      generateDiffBetweenTwoSnapshotDatas(
        maybeCurrentSnapshotData,
        newSnapshotData
      );

    snapshotDataDiffs.set(
      snapshotInfo.id,
      defensiveCopy(diffBetweenCurrentAndNewSnapshots)
    );
  }

  @Mutation
  overrideLastSnapshotMutation(newSnapshotData: SnapshotData) {
    const idx = this.snapshots.length - 1;
    const existingSnapshotInfo = this.snapshots[idx];

    const newSnapshotInfo = _snapshotInfoFromSnapshotData(newSnapshotData);

    this.snapshots[idx] = { ...newSnapshotInfo };

    const maybePreLastSnapshotData = regenerateSnapshotDataForIndex(
      idx - 1,
      this.snapshots
    );

    const diff = generateDiffBetweenTwoSnapshotDatas(
      maybePreLastSnapshotData,
      newSnapshotData
    );

    if (existingSnapshotInfo) {
      snapshotDataDiffs.delete(existingSnapshotInfo.id);
    }

    snapshotDataDiffs.set(newSnapshotInfo.id, defensiveCopy(diff));
  }

  @Mutation
  rewriteHistoryFromIndex(index: number) {
    const newSnapshotInfos = [];
    for (const [idx, snapshot] of this.snapshots.entries()) {
      if (idx >= index) {
        snapshotDataDiffs.delete(snapshot.id);
      } else {
        newSnapshotInfos.push(snapshot);
      }
    }
    this.snapshots = newSnapshotInfos;
  }

  get currentSnapshotInfo(): SnapshotInfo | undefined {
    return this.snapshots[this.index];
  }

  get currentSnapshot(): SnapshotData | undefined {
    return regenerateSnapshotDataForIndex(this.index, this.snapshots);
  }
}
